import Button from 'components/Button/Button';
import CronGenerator from 'components/CronGenerator/CronGenerator';
import Text from 'components/Text/Text';
import ArticleField from './components/ArticleField';
import ActivationScheduleInput from './components/ActivationScheduleInput';
import DeactivationScheduleInput from './components/DeactivationScheduleInput';
import React, { useContext, useEffect } from 'react';
import { Form } from 'antd';
import BtnBlock from './components/BtnBlock';
import NameField from './components/NameField';
import CustomerSelect from './components/CustomerSelect';
import DescriptionField from './components/DescriptionField';
import RestaurantPricesField from './components/RestaurantPricesField/RestaurantPricesField';
import ModifierGroupField from './components/ModifierGroupField/ModifierGroupField';
import IsDisabledField from './components/IsDisabledField/IsDisabledField';
import IsRestrictedField from './components/IsRestrictedField/IsRestrictedField';
import IsPreselectedField from './components/IsPreselectedField/IsPreselectedField';
import QuantityRegulationField from './components/QuantityRegulationField/QuantityRegulationField';
import ExternalIdField from './components/ExternalIdField';
import { IRestaurantModifierForCreateModifier } from 'types';
import { EditModifierContext, IEditModifierContext } from '../../../../../editModifierState/editModifierContext';
import { getFileIdForSubmit, preventDefaultForEnterKey } from 'utils/helpers';
import style from './InputFields.module.scss';

const InputFields: React.FC = () => {
	const {
		changeTypeOfModalWindow,
		setDataForSubmit,
		changeName,
		changeExternalId,
		generateUniqueExternalId,
		changeDescription,
		setRestaurantsForPrice,
		setFetchedRestaurants,
		changePrice,
		addPrices,
		removePrice,
		setIDsIsForceDisabledRestaurant,
		setModifierGroups,
		setFetchedModifierGroups,
		updateModifierGroups,
		setDisabledInRestaurants,
		setRestrictedInRestaurants,
		setPreselectedInRestaurants,
		setQuantityRegulationRestaurants,
		changeQuantityMaxLimit,
		changeActivationSchedule,
		changeDeactivationSchedule,
		modifierId,
		fileId,
		filePath,
		selectedCustomer,
		selectedModifierGroups,
		name,
		articleNumber,
		description,
		externalId,
		restaurantsForPrice,
		price,
		totalPrices,
		IDsIsForceDisabledRestaurant,
		isDisabledInRestaurants,
		isRestrictedInRestaurants,
		isPreselectedInRestaurants,
		quantityRegulationRestaurants,
		quantityMaxLimit,
		translations,
		fetchedRestaurants,
		fetchedModifierGroups,
		activationSchedule,
		deactivationSchedule,
		defaultCustomerLanguage,
	} = useContext<IEditModifierContext>(EditModifierContext);
	const [form] = Form.useForm();

	useEffect(() => {
		form.setFieldsValue({
			selectedCustomer,
			selectedModifierGroups,
			name,
			articleNumber,
			description,
			externalId,
			restaurantsForPrice,
			price,
			isDisabledInRestaurants,
			isRestrictedInRestaurants,
			isPreselectedInRestaurants,
			quantityRegulationRestaurants,
			quantityMaxLimit,
			activationSchedule,
			deactivationSchedule,
		});
	}, [
		selectedCustomer,
		selectedModifierGroups,
		name,
		articleNumber,
		description,
		externalId,
		restaurantsForPrice,
		price,
		isDisabledInRestaurants,
		isRestrictedInRestaurants,
		isPreselectedInRestaurants,
		quantityRegulationRestaurants,
		quantityMaxLimit,
		activationSchedule,
		deactivationSchedule,
	]);

	const submit = () => {
		const defaultTranslationIsEmpty =
			translations.find((translation) => translation.languageId === defaultCustomerLanguage?.id)?.name === '';
		if (defaultTranslationIsEmpty) {
			changeTypeOfModalWindow('defaultTranslationIsEmpty');
			return;
		}

		const finalRestaurantModifiers: IRestaurantModifierForCreateModifier[] = totalPrices.map((el) => {
			const isDisabled = isDisabledInRestaurants.find(
				(disabledRestaurant) => disabledRestaurant === el.restaurantId,
			);
			const isRestricted = isRestrictedInRestaurants.find(
				(restrictedRestaurant) => restrictedRestaurant === el.restaurantId,
			);
			const isPreselected = isPreselectedInRestaurants.find(
				(preselectedRestaurant) => preselectedRestaurant === el.restaurantId,
			);
			const hasQuantityMaxLimit = quantityRegulationRestaurants.find(
				(quantityRegulationRestaurant) => quantityRegulationRestaurant === el.restaurantId,
			);
			return {
				restaurantId: el.restaurantId,
				isDisabled: !!isDisabled,
				isRestricted: !!isRestricted,
				isPreselected: !!isPreselected,
				price: Number(el.price),
				quantityMaxLimit: hasQuantityMaxLimit ? quantityMaxLimit : null,
				forceDisable: IDsIsForceDisabledRestaurant.includes(el.restaurantId),
			};
		});

		setDataForSubmit({
			id: modifierId,
			fileId: getFileIdForSubmit({ fileId, filePath }),
			name,
			description,
			customerId: selectedCustomer || '',
			restaurantsModifiers: finalRestaurantModifiers,
			modifierGroupIds: selectedModifierGroups,
			externalId: externalId ? externalId : null,
			activationCron: activationSchedule || null,
			deactivationCron: deactivationSchedule || null,
			translations,
		});
		changeTypeOfModalWindow('toConfirmSubmit');
	};

	const revalidateForm = form.validateFields;

	return (
		<div className={style.container} data-test="InputFields">
			<Form form={form} name="basic" onFinish={submit} onKeyPress={preventDefaultForEnterKey} layout="vertical">
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="mainInfoHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<CustomerSelect />
					<NameField changeName={changeName} />
					<ArticleField />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="idHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<div className={style.row}>
						<ExternalIdField changeExternalId={changeExternalId} />
						<Button onClick={generateUniqueExternalId} theme="primary">
							<Text
								translationNameSpace="form"
								translationKey="generateUniqueExternalId"
								asFragment={true}
							/>
						</Button>
					</div>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="descriptionHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<DescriptionField changeDescription={changeDescription} />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="restaurantsModifierGroupsHeadingHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<RestaurantPricesField
						restaurantsForPrice={restaurantsForPrice}
						price={price}
						totalPrices={totalPrices}
						IDsIsForceDisabledRestaurant={IDsIsForceDisabledRestaurant}
						selectedCustomer={selectedCustomer}
						fetchedRestaurants={fetchedRestaurants}
						externalId={externalId}
						revalidateForm={revalidateForm}
						setRestaurantsForPrice={setRestaurantsForPrice}
						changePrice={changePrice}
						removePrice={removePrice}
						setIDsIsForceDisabledRestaurant={setIDsIsForceDisabledRestaurant}
						setFetchedRestaurants={setFetchedRestaurants}
						addPrices={addPrices}
					/>
					<ModifierGroupField
						selectedModifierGroups={selectedModifierGroups}
						totalPrices={totalPrices}
						fetchedModifierGroups={fetchedModifierGroups}
						updateModifierGroups={updateModifierGroups}
						setFetchedModifierGroups={setFetchedModifierGroups}
						setModifierGroups={setModifierGroups}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="inStopHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<IsDisabledField
						isDisabledInRestaurants={isDisabledInRestaurants}
						totalPrices={totalPrices}
						fetchedRestaurants={fetchedRestaurants}
						externalId={externalId}
						setDisabledInRestaurants={setDisabledInRestaurants}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="isRestrictedHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<IsRestrictedField
						totalPrices={totalPrices}
						isRestrictedInRestaurants={isRestrictedInRestaurants}
						fetchedRestaurants={fetchedRestaurants}
						setRestrictedInRestaurants={setRestrictedInRestaurants}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="preselectedModifiersHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<IsPreselectedField
						totalPrices={totalPrices}
						isPreselectedInRestaurants={isPreselectedInRestaurants}
						fetchedRestaurants={fetchedRestaurants}
						setPreselectedInRestaurants={setPreselectedInRestaurants}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="quantityRegulationHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<QuantityRegulationField
						setQuantityRegulationRestaurants={setQuantityRegulationRestaurants}
						changeQuantityMaxLimit={changeQuantityMaxLimit}
						quantityRegulationRestaurants={quantityRegulationRestaurants}
						fetchedRestaurants={fetchedRestaurants}
						totalPrices={totalPrices}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="scheduleHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<div className={style.row}>
						<ActivationScheduleInput changeActivationSchedule={changeActivationSchedule} />
						<DeactivationScheduleInput changeDeactivationSchedule={changeDeactivationSchedule} />
					</div>
					<CronGenerator />
				</div>
				<BtnBlock />
			</Form>
		</div>
	);
};

export default InputFields;
